import { z } from "zod";

/** A published v1 page the customer can bring into their Agent site. */
export const v1MigrationPageSchema = z
  .object({
    elementId: z.string(),
    name: z.string(),
    projectName: z.string().nullable(),
    shopifyPagePath: z.string(),
    publishedAt: z.string().nullable(),
    /** Where the page lands in the Agent site. */
    route: z.string(),
  })
  .describe("V1MigrationPage");

export const v1MigrationPagesSchema = z
  .object({
    pages: z.array(v1MigrationPageSchema),
    /** Product loaders read the merchant's catalog, so a page migrated without it has no product data. */
    hasShopifyConnection: z.boolean(),
    /** Pages came over without a store, and they check out through Shopify, so Home keeps a Connect Shopify task. */
    shopifyCheckoutPending: z.boolean(),
  })
  .describe("V1MigrationPages");

export const v1MigrationJobOutcomeSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("migrated"),
      route: z.string(),
      pageUrl: z.string(),
      /** Interactions and data the compiler left unfinished, which the agent can finish when asked in chat. */
      todoCount: z.number(),
      /** False when nothing reached the site's repository: no working tree, or the push failed. */
      written: z.boolean(),
    }),
    z.object({ kind: z.literal("missingLiveSnapshot"), message: z.string() }),
    z.object({ kind: z.literal("routeExists"), message: z.string() }),
  ])
  .describe("V1MigrationJobOutcome");

export const v1MigrationJobViewSchema = z
  .object({
    jobId: z.string(),
    elementId: z.string(),
    status: z.enum(["queued", "in_progress", "completed", "failed"]),
    step: z.enum(["compiling", "writing"]).nullable(),
    outcome: v1MigrationJobOutcomeSchema.nullable(),
    error: z.string().nullable(),
  })
  .describe("V1MigrationJobView");

/** One job per page, compiled one after another in the site's sandbox. */
export const v1MigrationStartedSchema = z
  .object({
    jobs: z.array(z.object({ jobId: z.string(), elementId: z.string() })),
  })
  .describe("V1MigrationStarted");

/**
 * A migrated site's shared v1 components and their props, as the site compile writes them for the naming
 * pass (compile-site.mjs --naming-context): each prop's auto name (dotted for a list item's field), its kind,
 * the v1 layer it was named after, a few values pages pass and the line that reads it.
 */
export const v1SiteNamingContextSchema = z
  .object({
    components: z.array(
      z.object({
        /** the group key decisions.json renames by */
        key: z.string(),
        component: z.string(),
        v1Name: z.string(),
        props: z.array(
          z.object({
            name: z.string(),
            kind: z.string(),
            layer: z.string().nullable(),
            samples: z.array(z.string()),
            excerpt: z.string(),
          }),
        ),
      }),
    ),
  })
  .describe("V1SiteNamingContext");

/** Per group key, auto prop name → the name to use (a bare identifier, also for a list item's field). */
export const v1SitePropNamesSchema = z
  .object({
    groups: z.record(z.string(), z.record(z.string(), z.string())),
  })
  .describe("V1SitePropNames");
